/**
 * findTailTip — T2 step 2 (ADR-031 §3.3): **the tip is the flesh farthest from
 * the root, measured through the tail.**
 *
 * ONE responsibility: run the geodesic field from the root voxel over the tail
 * grid and return the farthest voxel the root can REACH. A curled tail needs
 * no pin — the field follows the curl, where a Euclidean "farthest point"
 * would pick whichever loop of the curl happens to swing widest.
 *
 * REACHABLE, NOT RAW (Codex P2 on the design note, 2026-09-04). The zone
 * schema does not demand connectivity, and `geodesicField` leaves what the
 * root cannot reach at `Infinity` — a raw argmax would crown a detached scrap
 * (a stray plate edge, a flap the voxelizer separated) as the tip. So the
 * argmax runs over finite distances only, and every unreachable component is
 * REPORTED with its voxel count as a zone defect, never chosen and never
 * silent.
 *
 * A person's tip (`target.tails[].tip`) wins when given — the pin is the
 * correction — and is snapped to the nearest reachable voxel; a pin nothing
 * reachable is near refuses by name.
 *
 * A FAN refuses: when the field has more than one far, prominent maximum
 * (zones/prominent-tips), more than one tail leaves this root — a kitsune —
 * and one chain down one of them would pin the rest to it.
 *
 * Everything is VOXEL-INDEX space. Depends on: hands/bind-weights
 * (geodesicField — the one BFS), hands/voxel-grid, zones/prominent-tips. Depended on by:
 * tails/place-tail.
 */
import { geodesicField } from '../hands/bind-weights.js';
import { voxelCoords, voxelIndex, type VolumeGrid, type VoxelPoint } from '../hands/voxel-grid.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';
import { TailFanRefusal, TailRefusal } from './isolate-zone.js';
import { prominentTips } from '../zones/prominent-tips.js';

const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/** The fan refusal, as a person reads it. */
export function tailFanRefusal(tails: number): string {
  const others = tails - 1;
  return (
    `${tails} tails share this root; growing one chain would pin ${COUNT_WORDS[others] ?? others} of them to it. ` +
    `Multi-tail rigging isn't supported yet.`
  );
}

export interface TailTip {
  /** The tip voxel's centre, voxel-index space. */
  readonly tip: VoxelPoint;
  /** Where the field started (the occupied voxel nearest the root). */
  readonly rootVoxel: VoxelPoint;
  /** Geodesic (6-connected) distance root → tip, in voxels. */
  readonly geodesicVoxels: number;
  /** Occupied voxels the root reaches. */
  readonly reachableVoxels: number;
  /** Unreachable components, largest first — zone defects, reported not chosen. */
  readonly scraps: readonly { readonly voxels: number; readonly centroid: VoxelPoint }[];
  /** True when a person's pinned tip was used. */
  readonly pinned: boolean;
  /**
   * A GROUPED fan's tips (two or more), voxel space; `tip` is then their mean.
   * Empty for a single tail.
   */
  readonly fanTips: readonly VoxelPoint[];
  readonly markers: readonly EvidenceMarker[];
}

function nearestOccupied(p: VoxelPoint, list: readonly number[], dims: readonly [number, number, number], ok: (slot: number) => boolean): { voxel: number; d2: number } {
  let best = -1;
  let bestD2 = Infinity;
  list.forEach((v, s) => {
    if (!ok(s)) return;
    const [x, y, z] = voxelCoords(v, dims);
    const d2 = (x - p.x) ** 2 + (y - p.y) ** 2 + (z - p.z) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = v;
    }
  });
  return { voxel: best, d2: bestD2 };
}

/**
 * Find the tail's tip. `pinnedTip` (voxel space) replaces the argmax when a
 * person placed it. `zoneName` names the refusal.
 */
export function findTailTip(
  grid: VolumeGrid,
  root: VoxelPoint,
  zoneName: string,
  pinnedTip?: VoxelPoint,
  /**
   * Voxels that may count as a fan's tips: the tail side of the root cut. Flesh
   * lying ON the body's plane (a swallowed annulus around the root) has rim
   * points the 6-connected field reads as far, and is no tail. Default: all.
   */
  beyondRoot?: (voxel: number) => boolean,
  /**
   * 'grouped': a fan is rigged as ONE chain through the bundle's mass
   * (bundle-path) instead of refused. 'ignore': a STUB — a puff's dome has many
   * equally far points, and counting them as tails is noise (the bear girl's
   * bob, 2026-09-25: "4 tails share this root").
   */
  fanMode: 'refuse' | 'grouped' | 'ignore' = 'refuse',
): TailTip {
  const dims = grid.dims as readonly [number, number, number];
  const list = [...grid.occupied].sort((a, b) => a - b);
  const slot = new Int32Array(dims[0] * dims[1] * dims[2]).fill(-1);
  list.forEach((v, i) => {
    slot[v] = i;
  });
  const start = nearestOccupied(root, list, dims, () => true);
  if (start.voxel < 0) throw new TailRefusal(`zone "${zoneName}" voxelized to nothing — no tail to walk.`);
  const field = geodesicField([start.voxel], dims, slot, list);

  let reachable = 0;
  let far = -1;
  let farD = -1;
  field.forEach((d, s) => {
    if (!Number.isFinite(d)) return;
    reachable++;
    // Ties break toward the lower index — deterministic (ADR-005 §9).
    if (d > farD) {
      farD = d;
      far = list[s]!;
    }
  });

  // --- a FAN: more than one tail from this root (the kitsune, 2026-09-24) ----------
  // One chain down one tail of five would pin the other four to it — refused,
  // pinned tip or not. Counted as the field's far, prominent maxima.
  const fanField = beyondRoot === undefined ? field : field.map((d, s) => (beyondRoot(list[s]!) ? d : Infinity));
  const fan = prominentTips(fanField, (s) => {
    const [x, y, z] = voxelCoords(list[s]!, dims);
    const out: number[] = [];
    for (const [dx, dy, dz] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
      const ax = x + dx;
      const ay = y + dy;
      const az = z + dz;
      if (ax < 0 || ay < 0 || az < 0 || ax >= dims[0] || ay >= dims[1] || az >= dims[2]) continue;
      const ns = slot[voxelIndex(ax, ay, az, dims)]!;
      if (ns >= 0) out.push(ns);
    }
    return out;
  });
  const fanTips: VoxelPoint[] = [];
  let fanSpread = 0;
  if (fan.tips.length >= 2 && fanMode !== 'ignore') {
    if (fanMode === 'refuse') throw new TailFanRefusal(`zone "${zoneName}": ${tailFanRefusal(fan.tips.length)}`, fan.tips.length);
    const [rx0, ry0, rz0] = voxelCoords(start.voxel, dims);
    for (const s of fan.tips) {
      const [x, y, z] = voxelCoords(list[s]!, dims);
      fanTips.push({ x, y, z });
    }
    const dirs = fanTips.map((t) => {
      const v = { x: t.x - rx0, y: t.y - ry0, z: t.z - rz0 };
      const l = Math.hypot(v.x, v.y, v.z) || 1;
      return { x: v.x / l, y: v.y / l, z: v.z / l };
    });
    // The spread is EVIDENCE, not the gate: Kitty's tips sit 147° apart and her
    // spine still runs in flesh (place-tail refuses on the spine itself).
    for (let i = 0; i < dirs.length; i++) {
      for (let j = i + 1; j < dirs.length; j++) {
        const a = dirs[i]!;
        const b = dirs[j]!;
        const c = a.x * b.x + a.y * b.y + a.z * b.z;
        const cross = Math.hypot(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
        fanSpread = Math.max(fanSpread, (Math.atan2(cross, c) * 180) / Math.PI);
      }
    }
  }

  // --- unreachable components: reported, never chosen ---------------------------
  const scraps: { voxels: number; centroid: VoxelPoint }[] = [];
  const seen = new Uint8Array(list.length);
  for (let s = 0; s < list.length; s++) {
    if (Number.isFinite(field[s]!) || seen[s] === 1) continue;
    const queue = [list[s]!];
    seen[s] = 1;
    let n = 0;
    let sx = 0;
    let sy = 0;
    let sz = 0;
    while (queue.length > 0) {
      const cur = queue.pop()!;
      const [x, y, z] = voxelCoords(cur, dims);
      n++;
      sx += x;
      sy += y;
      sz += z;
      for (const [dx, dy, dz] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
        const ax = x + dx;
        const ay = y + dy;
        const az = z + dz;
        if (ax < 0 || ay < 0 || az < 0 || ax >= dims[0] || ay >= dims[1] || az >= dims[2]) continue;
        const ns = slot[voxelIndex(ax, ay, az, dims)]!;
        if (ns < 0 || seen[ns] === 1) continue;
        seen[ns] = 1;
        queue.push(list[ns]!);
      }
    }
    scraps.push({ voxels: n, centroid: { x: sx / n, y: sy / n, z: sz / n } });
  }
  scraps.sort((a, b) => b.voxels - a.voxels);
  const markers: EvidenceMarker[] = [];
  if (fanTips.length > 0) {
    markers.push({ code: 'tail-fan-grouped', detail: { zone: zoneName, tails: fanTips.length, spreadDeg: Number(fanSpread.toFixed(1)) } });
  }
  if (scraps.length > 0) {
    markers.push({
      code: 'tail-zone-unreachable-scraps',
      detail: { zone: zoneName, scraps: scraps.length, voxels: scraps.map((s) => s.voxels).join(', ') },
    });
  }

  let tipVoxel = far;
  let pinned = false;
  if (pinnedTip !== undefined) {
    const snapped = nearestOccupied(pinnedTip, list, dims, (s) => Number.isFinite(field[s]!));
    // Two voxels of slack: a tip is a SURFACE point and quantizes outside its cell.
    if (snapped.voxel < 0 || snapped.d2 > 4) {
      throw new TailRefusal(
        `zone "${zoneName}": the pinned tip is ${Math.sqrt(snapped.d2).toFixed(1)} voxels from any flesh the root reaches — pin it on the tail.`,
      );
    }
    tipVoxel = snapped.voxel;
    pinned = true;
  }
  const [tx, ty, tz] = voxelCoords(tipVoxel, dims);
  const [rx, ry, rz] = voxelCoords(start.voxel, dims);
  // A grouped fan ends at the MEAN of its tips (a point in the bundle, not on one tail); a pinned tip still wins.
  const grouped = fanTips.length > 0 && !pinned;
  const mean = (k: 'x' | 'y' | 'z'): number => fanTips.reduce((a, t) => a + t[k], 0) / fanTips.length;
  return {
    tip: grouped ? { x: mean('x'), y: mean('y'), z: mean('z') } : { x: tx, y: ty, z: tz },
    rootVoxel: { x: rx, y: ry, z: rz },
    geodesicVoxels: field[slot[tipVoxel]!]!,
    reachableVoxels: reachable,
    scraps,
    pinned,
    fanTips: grouped ? fanTips : [],
    markers,
  };
}
